import styles from '@/styles/datagrid.module.scss';
import { type GridCellParams } from '@mui/x-data-grid-premium';
import isUndefined from 'lodash/isUndefined';

import { isHistoryTime } from './date-helpers';
import { type PositionsByDate } from './report-table-data-processor';

const getDescSortedPositionsByDateKeys = (positionsByDate?: PositionsByDate | null) =>
  positionsByDate
    ? Object.keys(positionsByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    : [];

export const getTodayAndYesterdayDateKey = (
  positionsByDate?: PositionsByDate | null,
): [string?, string?] => {
  if (!positionsByDate) return [undefined, undefined];

  const nonHistoryPositionsByDate = Object.keys(positionsByDate).filter(
    (item) => !isHistoryTime(item),
  );
  const nonHistoryPositionsByDateSorted = nonHistoryPositionsByDate.sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return [nonHistoryPositionsByDateSorted[0], nonHistoryPositionsByDateSorted[1]];
};

export const getTodayAndYesterdayValues = (positionsByDate?: PositionsByDate | null): number[] => {
  const todayAndYesterdayValues: number[] = [];
  let i = 0;

  for (const _ISODateString of getDescSortedPositionsByDateKeys(positionsByDate)) {
    if (todayAndYesterdayValues.length === 2 || i === 2) {
      break;
    }

    const { organicPosition } = positionsByDate?.[_ISODateString] || {};

    if (isNumber(organicPosition) && organicPosition <= 99990 && isFinite(organicPosition)) {
      todayAndYesterdayValues.push(organicPosition);
    }

    i += 1;
  }

  return todayAndYesterdayValues;
};

export const getPromotionRowTopValue = (
  params: GridCellParams,
  boost: boolean = false,
): number | null => {
  const positions = params.row.positionsByDate;

  if (!positions) return null;

  const keys = getDescSortedPositionsByDateKeys(positions);
  let top: number | null = null;

  for (const key of keys) {
    const { organicPosition, beforePromotion } = positions[key] || {};
    const currentPosition = boost ? organicPosition : beforePromotion;

    if (isNumber(currentPosition) && currentPosition <= 99990 && isFinite(currentPosition)) {
      if (top === null || currentPosition < top) {
        top = currentPosition;
      }
    }
  }

  return top;
};

export const getPromotionRowDelta = (params: GridCellParams): number | null => {
  const todayAndYesterdayValues = getTodayAndYesterdayValues(params.row.positionsByDate);

  if (todayAndYesterdayValues.length !== 2) return null;

  const delta = todayAndYesterdayValues[1] - todayAndYesterdayValues[0];
  
  // Проверяем, что результат является валидным числом (не NaN, не Infinity)
  return isFinite(delta) ? delta : null;
};

export const getPromotionRowDeltaOfTop = (
  params: GridCellParams,
  boost: boolean = false,
): number | null => {
  const todayAndYesterdayValues = getTodayAndYesterdayValues(params.row.positionsByDate);

  if (isNumber(todayAndYesterdayValues[0]) || isUndefined(todayAndYesterdayValues[0])) return null;

  const top = getPromotionRowTopValue(params, boost);

  // Проверяем, что top является валидным числом
  if (top === null || !isNumber(top)) return null;

  const delta = top - todayAndYesterdayValues[0];
  
  // Проверяем, что результат является валидным числом (не NaN, не Infinity)
  return isFinite(delta) ? delta : null;
};

export const getTodaysPromotionCPM = (params: GridCellParams): number | null => {
  const [todayKey] = getTodayAndYesterdayDateKey(params.row.positionsByDate);

  return todayKey && params.row.positionsByDate?.[todayKey]
    ? (params.row.positionsByDate[todayKey].cpm ?? null)
    : null;
};

export const getTodaysPromotionBoost = (params: GridCellParams): number | null => {
  const [todayKey] = getTodayAndYesterdayDateKey(params.row.positionsByDate);
  const promotionJump = todayKey && params.row.positionsByDate?.[todayKey]?.promotionJump;

  if (!isNumber(promotionJump)) return null;
  
  const absValue = Math.abs(promotionJump);

  return isFinite(absValue) ? absValue : null;
};

interface IColorSort {
  max: number;
  max2: number;
  min2: number;
  min: number;
}

export const getSortValuesForClassName = (values: (number | undefined)[]): IColorSort => {
  const preparedValues = isObject(values[0]) ? values.map((value) => value?.min) : values;
  const sortedAll = preparedValues
    .filter((item: number | undefined) => item !== undefined)
    .sort((a, b) => b - a);

  const sorted = [...new Set(sortedAll)];

  if (sorted.length === 1) {
    return {
      max: sorted[0],
      max2: sorted[0] - 1,
      min2: 0,
      min: 0,
    };
  }

  if (sorted.length <= 4) {
    return {
      max: sorted?.[0],
      max2: sorted?.[1] || 0,
      min2: sorted?.[2] || 0,
      min: sorted?.[3] || 0,
    };
  }

  return {
    max: sorted[0],
    max2: sorted[1],
    min2: sorted[sorted.length - 2],
    min: sorted[sorted.length - 1],
  };
};

export const getDataGridCellColorClass = (
  sort: IColorSort | undefined,
  value: string | number | undefined | { min: number; max: number },
): string => {
  if (sort === undefined) {
    return styles.row_gray_light;
  }

  if (!sort) {
    return styles.cell_yellow;
  }

  const preparedValue = isObject(value) && value !== null ? value.min : Number(value);

  if (preparedValue === undefined || preparedValue === 0) {
    return styles.cell_gray;
  }

  if (preparedValue === sort.max) {
    return styles.cell_dark_green;
  }

  if (preparedValue === sort.max2) {
    return styles.cell_light_green;
  }

  if (preparedValue === sort.min2) {
    return styles.cell_light_red;
  }

  if (preparedValue === sort.min) {
    return styles.cell_dark_red;
  }

  return styles.cell_yellow;
};

export const getDataGridCellColorClassInverse = (
  sort: IColorSort | undefined,
  value: string | number | undefined | { min: number; max: number },
): string => {
  if (sort === undefined) {
    return styles.row_gray_light;
  }

  if (!sort) {
    return styles.cell_yellow;
  }

  const preparedValue = isObject(value) && value !== null ? value.min : Number(value);

  if (preparedValue === undefined || preparedValue === 0) {
    return styles.cell_gray;
  }

  switch (preparedValue) {
    case sort.max:
      return styles.cell_dark_red;
    case sort.max2:
      return styles.cell_light_red;
    case sort.min2:
      return styles.cell_light_green;
    case sort.min:
      return styles.cell_dark_green;
    default:
      return styles.cell_yellow;
  }
};
